import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, map, Observable, of } from 'rxjs';
import { getFullUrl } from 'src/app/shared/utils/url';
import { UserInfo } from './user-info.model';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserInfoService {
  private http: HttpClient;
  private _userInfoSubject = new BehaviorSubject<UserInfo | undefined>(
    undefined
  );
  public get userInfo(): Observable<UserInfo | undefined> {
    return this._userInfoSubject;
  }

  constructor(
    private translate: TranslateService,
    private router: Router,
    private cookieService: CookieService,
    handler: HttpBackend
  ) {
    // Prevents the interceptors from intercepting requests made by this service
    // Against circular calls after error handling
    this.http = new HttpClient(handler);
  }

  public refreshUserInfo(): Observable<UserInfo | undefined> {
    return this.http
      .get<UserInfo>(
        getFullUrl(environment.guiBackendRootEndpoint, 'user-info'),
        {
          observe: 'response',
          headers: {
            'X-AUTH-TOKEN': this.cookieService.get('session_id') ?? '',
          },
        }
      )
      .pipe(
        map((userInfoResponse) => {
          const userInfo = userInfoResponse.body ?? undefined;
          this._userInfoSubject.next(userInfo);
          return userInfo;
        }),
        catchError((error) => {
          this.clearUserInfo();
          if (error.status === 401 || error.status === 403) {
            this.router.navigate(['/no-permission']).catch(console.error);
          }
          return of(undefined);
        })
      );
  }

  public refreshUserInfoIfUndefined(): Observable<UserInfo | undefined> {
    if (this._userInfoSubject.value === undefined) {
      return this.refreshUserInfo();
    }
    return this._userInfoSubject;
  }

  public clearUserInfo() {
    this._userInfoSubject.next(undefined);
  }

  public get memberOf() {
    return this._userInfoSubject.value?.memberOf;
  }

  public get userFullName() {
    const { givenName, familyName } = this._userInfoSubject.value ?? {};
    return this.translate.instant('header.username', { givenName, familyName });
  }

  public get userGivenName() {
    return this._userInfoSubject.value?.givenName;
  }
}
